// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isCartEmpty } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React, { FC, useContext, useState } from "react";
import QuickAddProduct, { QuickAddProductPresentationProps } from "@store/Library/Components/QuickAddProduct";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import { useCurrentCartState } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import PowerBolt from "@store/Library/Components/Icons/PowerBolt";
import constants from "@store/constants";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { css } from "styled-components";

const awaitableAddToCart = makeHandlerChainAwaitable(addToCart);

export interface CartQuickAddProps extends WidgetProps {}

export interface CartQuickAddStyles {
    quickAddProduct?: QuickAddProductPresentationProps;
}

export const cartQuickAddStyles: CartQuickAddStyles = {
    quickAddProduct: {
        iconSrc: PowerBolt,
        extendedStyles: {
            accordionTitleIcon: { size: 28 },
            accordionTitle: {
                css: css`
                    font-size: ${pxToRem(20)};
                `,
            },
        },
    },
};

const styles = cartQuickAddStyles;

export const CartQuickAdd: FC<CartQuickAddProps> = () => {
    const showAddToCartConfirmationDialog = useAppSelector(
        state => getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    );
    const allowQuickOrder = useAppSelector(state => getSettingsCollection(state).orderSettings.allowQuickOrder);
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>("");
    const trackAddToCart = ga.use.trackAddToCart();
    const cartDataView = useCurrentCartState();
    const cartIsEmpty = isCartEmpty(cartDataView.value);
    const isMobile = useIsBreakpoint("sm", "down");
    // On desktop: always start expanded. On mobile: expanded only when cart is empty.
    const initialExpanded = !isMobile || cartIsEmpty;

    if (!allowQuickOrder) {
        return null;
    }

    const addProductToCart = async (productInfo: ProductInfo, product: ProductModel) => {
        if (
            !product.quoteRequired &&
            !product.allowZeroPricing &&
            productInfo.pricing &&
            getUnitNetPrice(productInfo.pricing, 1).price === 0
        ) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const cartline = (await dispatch(
            awaitableAddToCart({
                productId: productInfo.productId,
                qtyOrdered: productInfo.qtyOrdered,
                unitOfMeasure: productInfo.unitOfMeasure,
                onError: () => {
                    setErrorMessage(siteMessage("Product_NotFound"));
                },
            }),
        )) as CartLineModel;

        trackAddToCart({ items: [product] }, { productInfos: [productInfo] });

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    return (
        <QuickAddProduct
            {...styles.quickAddProduct}
            title={translate("Quick Order")}
            subtitle={<>—&nbsp;{translate("quickly add any product from your catalog")}</>}
            initialExpanded={initialExpanded}
            productSelectorProps={{
                selectButtonTitle: translate("Add to Cart"),
                onSelectProduct: addProductToCart,
                productIsConfigurableMessage: siteMessage("QuickOrder_CannotOrderConfigurable"),
                productIsUnavailableMessage: siteMessage("QuickOrder_ProductIsUnavailable"),
                customErrorMessage: errorMessage,
            }}
            data-test-selector="quickOrderWidget"
        />
    );
};

const widgetModule: WidgetModule = {
    component: CartQuickAdd,
    definition: {
        group: "Cart",
        icon: "cart-shopping-fast",
        displayName: "Cart Quick Order",
        allowedContexts: [constants.pageType.cart],
    },
};

export default widgetModule;
